import { acClient } from '@/clients/ac-client.js';
import { codesService, type Code } from '@/services/codes.js';
import { backboneService } from '@/services/backbone.js';
import { toastService } from '@/services/toast.js';
import { HatElement } from '@/components/hat-element.js';

class HatAdminInvitationForm extends HatElement {
  private _types: Code[] = [];

  async connectedCallback() {
    super.connectedCallback();
    await this._loadCodes();
    this._renderShell();
  }

  async _loadCodes() {
    try {
      this._types = await codesService.getDomain('ac.invitation.type');
    } catch {
      toastService.show('Failed to load code options.');
    }
  }

  _renderShell() {
    this.root.innerHTML = `
      <style>
        h2 {
          color: white;
          font-size: 1.4rem;
          margin: 0 0 24px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .back-link {
          display: inline-block;
          margin-bottom: 16px;
          color: rgba(255,255,255,0.8);
          cursor: pointer;
          font-size: 0.9rem;
          background: none;
          border: none;
          padding: 0;
          text-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }

        .back-link:hover { color: white; }

        .card {
          background: white;
          border-radius: 12px;
          padding: 28px;
          max-width: 480px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .field { margin-bottom: 20px; }

        label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #444;
          margin-bottom: 6px;
        }

        input, select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.95rem;
          box-sizing: border-box;
          outline: none;
        }

        input:focus, select:focus { border-color: #764ba2; }

        .actions {
          display: flex;
          gap: 12px;
          margin-top: 8px;
        }

        .btn-primary {
          background: #764ba2;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.95rem;
        }

        .btn-primary:hover { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.5; cursor: default; }

        .btn-cancel {
          background: none;
          border: 1px solid #ddd;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.95rem;
          color: #555;
        }

        .btn-cancel:hover { background: #f5f5f5; }
      </style>

      <button class="back-link">← Back to Invitations</button>
      <h2>New Invitation</h2>

      <div class="card">
        <div class="field">
          <label for="email">Email address</label>
          <input type="email" id="email" placeholder="user@example.com" />
        </div>
        <div class="field">
          <label for="type">Type</label>
          <select id="type">
            ${this._types.map(t => `<option value="${t.code}">${t.label}</option>`).join('')}
          </select>
        </div>
        <div class="actions">
          <button class="btn-primary" id="submit-btn">Send Invitation</button>
          <button class="btn-cancel" id="cancel-btn">Cancel</button>
        </div>
      </div>
    `;

    this.root.querySelector('.back-link')!.addEventListener('click', () => {
      backboneService.navigateTo('/admin/invitations');
    });

    this.root.querySelector<HTMLButtonElement>('#cancel-btn')!.addEventListener('click', () => {
      backboneService.navigateTo('/admin/invitations');
    });

    const submitBtn = this.root.querySelector<HTMLButtonElement>('#submit-btn')!;
    submitBtn.addEventListener('click', async () => {
      const email = this.root.querySelector<HTMLInputElement>('#email')!.value.trim();
      const type = this.root.querySelector<HTMLSelectElement>('#type')!.value;

      if (!email) {
        toastService.show('Email is required.');
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';

      try {
        await acClient.createInvitation(email, type);
        toastService.show('Invitation sent.', 'success');
        backboneService.navigateTo('/admin/invitations');
      } catch {
        toastService.show('Failed to send invitation.');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Send Invitation';
      }
    });
  }
}

customElements.define('hat-admin-invitation-form', HatAdminInvitationForm);